import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { ScheduleType } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { State } from 'src/app/store';
import { collectionsSelector } from 'src/app/global/store/global.selectors';
import { takeUntil, map, tap, take, filter } from 'rxjs/operators';
import {
    GetCollectionsAction,
    GetCollectionsSuccessAction,
    GetCollectionsErrorAction,
    DeleteCollectionSuccessAction,
    DeleteCollectionErrorAction,
    DeleteCollectionAction,
    DuplicateCollectionAction,
    DuplicateCollectionSuccessAction,
    DuplicateCollectionErrorAction,
} from 'src/app/global/store/global.actions';
import { Collection } from 'src/app/global/services/collections/collections.model';
import { SHAPE_STYLES } from '../../forms/shape-select/bciti-shape-select.component';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

@Component({
    selector: 'app-collections-table',
    templateUrl: './collections-table.component.html',
    styleUrls: ['./collections-table.component.scss'],
})
export class CollectionsTableComponent implements OnInit, AfterViewInit, OnDestroy {
    destroy$ = new Subject();
    loading = false;
    get currentLang(): string {
        return this.translate.currentLang;
    }

    dataSource = new MatTableDataSource<Collection>();
    ScheduleType = ScheduleType;

    columnKeys = ['color', 'icon', 'name', 'areas', 'days', 'frequency', 'action'];
    actionMenuOptions = [];

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    activeSort: Sort = { active: '', direction: '' };

    constructor(
        public translate: TranslateService,
        private store: Store<State>,
        private actions$: Actions,
        private dialog: MatDialog,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.initTranslatedObjects();

        this.store
            .select(collectionsSelector)
            .pipe(
                takeUntil(this.destroy$),
                map((collections) => {
                    let formattedCollections = collections.map((collection) => {
                        return {
                            ...collection,
                            schedules: collection.schedules?.filter((schedule) => schedule.type === this.ScheduleType.DEFAULT) ?? [],
                        };
                    });
                    this.dataSource.data = formattedCollections;
                }),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(GetCollectionsAction, DuplicateCollectionAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(GetCollectionsSuccessAction, GetCollectionsErrorAction, DuplicateCollectionSuccessAction, DuplicateCollectionErrorAction),
                tap(() => (this.loading = false)),
            )
            .subscribe();

        this.translate.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.store.dispatch(GetCollectionsAction());
                    this.initTranslatedObjects();
                    this.paginator._intl.itemsPerPageLabel = this.translate.instant('common.paginator.itemsPerPage');
                }),
            )
            .subscribe();

        this.store
            .select(collectionsSelector)
            .pipe(
                take(1),
                filter((collections) => collections.length === 0),
                tap(() => this.store.dispatch(GetCollectionsAction())),
            )
            .subscribe();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (item: Collection, property: string) => {
            switch (property) {
                case 'name':
                    return item.name?.[this.translate.currentLang] || '';
                case 'areas':
                    return item.areas?.[0]?.['name'] || '';
                case 'days':
                    return item.schedules?.[0]?.days?.[0] || '';
                case 'frequency':
                    return item.schedules?.[0]?.frequency || '';
                default:
                    return '';
            }
        };
    }

    getShapeStyle(shape: string): object {
        return SHAPE_STYLES[shape];
    }

    initTranslatedObjects(): void {
        this.actionMenuOptions = [
            {
                label: this.translate.instant('pages.collections.list.table.action-menu.edit'),
                eventEmitterTag: 'edit',
            },
            {
                label: this.translate.instant('pages.collections.list.table.action-menu.duplicate'),
                eventEmitterTag: 'duplicate',
            },
            {
                label: this.translate.instant('pages.collections.list.table.action-menu.delete'),
                eventEmitterTag: 'delete',
            },
        ];
    }

    handleClick(tag: string, element: Collection) {
        switch (tag) {
            case 'edit':
                this.router.navigate([`collections/edit/${element._id}`]);
                return;
            case 'duplicate':
                this.handleDuplicate(element);
                return;
            case 'delete':
                return this.handleDelete(element);
            default:
                console.log('unknown tag', tag);
                return;
        }
    }

    getSortIcon(columnKey: string): string {
        if (this.activeSort.active !== columnKey || this.activeSort.direction === '') return 'unfold_more';
        return this.activeSort.direction === 'asc' ? 'expand_less' : 'expand_more';
    }

    onSortChange(sort: Sort): void {
        this.activeSort = sort;
    }

    handleDuplicate(collection: Collection) {
        this.store.dispatch(DuplicateCollectionAction({ collection }));
    }

    handleDelete(collection: Collection): void {
        // open confirm dialog
        const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
            autoFocus: false,
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translate.instant('pages.collections.dialogs.delete-collection-dialog.title'),
                body: this.translate.instant('pages.collections.dialogs.delete-collection-dialog.body', { name: collection.name[this.translate.currentLang] }),
                buttons: {
                    confirm: {
                        text: this.translate.instant('common.delete'),
                        class: 'bciti-button bciti-button--red',
                    },
                    cancel: {
                        text: this.translate.instant('common.cancel'),
                        class: 'bciti-button bciti-button--blue-inversed',
                    },
                },
            },
        });

        // handle user confirm
        confirmDialogRef.componentInstance.onSubmit.subscribe((confirm) => {
            if (confirm) {
                confirmDialogRef.componentInstance.loading = true;
                this.store.dispatch(DeleteCollectionAction({ collection }));

                this.actions$
                    .pipe(
                        ofType(DeleteCollectionSuccessAction, DeleteCollectionErrorAction),
                        take(1),
                        tap(() => (confirmDialogRef.componentInstance.loading = false)),
                        ofType(DeleteCollectionSuccessAction),
                        tap(() => confirmDialogRef.close()),
                    )
                    .subscribe();
            } else {
                confirmDialogRef.close();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
